import { __decorate } from 'tslib';
import { LEFT_ARROW, RIGHT_ARROW, SPACE, ENTER } from '@angular/cdk/keycodes';
import { NgIf } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Optional, ViewChild, Input, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, fromEvent } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i1 from 'ng-zorro-antd/core/config';
import { WithConfig } from 'ng-zorro-antd/core/config';
import * as i6 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { InputBoolean } from 'ng-zorro-antd/core/util';
import * as i4 from 'ng-zorro-antd/core/wave';
import { NzWaveModule } from 'ng-zorro-antd/core/wave';
import * as i5 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import * as i2 from '@angular/cdk/a11y';
import * as i3 from '@angular/cdk/bidi';
const _c0 = ["switchElement"];
function NzSwitchComponent_span_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "span", 8);
  }
}
function NzSwitchComponent_ng_container_5_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzCheckedChildren);
  }
}
function NzSwitchComponent_ng_container_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, NzSwitchComponent_ng_container_5_ng_container_1_Template, 2, 1, "ng-container", 9);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzCheckedChildren);
  }
}
function NzSwitchComponent_ng_template_6_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzUnCheckedChildren);
  }
}
function NzSwitchComponent_ng_template_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NzSwitchComponent_ng_template_6_ng_container_0_Template, 2, 1, "ng-container", 9);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzUnCheckedChildren);
  }
}
const NZ_CONFIG_MODULE_NAME = 'switch';
let NzSwitchComponent = /*#__PURE__*/(() => {
  class NzSwitchComponent {
    updateValue(value) {
      if (this.isChecked !== value) {
        this.isChecked = value;
        this.onChange(this.isChecked);
      }
    }
    focus() {
      this.focusMonitor.focusVia(this.switchElement.nativeElement, 'keyboard');
    }
    blur() {
      this.switchElement.nativeElement.blur();
    }
    constructor(nzConfigService, host, ngZone, cdr, focusMonitor, directionality) {
      this.nzConfigService = nzConfigService;
      this.host = host;
      this.ngZone = ngZone;
      this.cdr = cdr;
      this.focusMonitor = focusMonitor;
      this.directionality = directionality;
      this._nzModuleName = NZ_CONFIG_MODULE_NAME;
      this.isChecked = false;
      this.onChange = () => {};
      this.onTouched = () => {};
      this.nzLoading = false;
      this.nzDisabled = false;
      this.nzControl = false;
      this.nzCheckedChildren = null;
      this.nzUnCheckedChildren = null;
      this.nzSize = 'default';
      this.nzId = null;
      this.dir = 'ltr';
      this.destroy$ = new Subject();
      this.isNzDisableFirstChange = true;
    }
    ngOnInit() {
      this.directionality.change.pipe(takeUntil(this.destroy$)).subscribe(direction => {
        this.dir = direction;
        this.cdr.detectChanges();
      });
      this.dir = this.directionality.value;
      this.ngZone.runOutsideAngular(() => {
        fromEvent(this.host.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(event => {
          event.preventDefault();
          if (this.nzControl || this.nzDisabled || this.nzLoading) {
            return;
          }
          this.ngZone.run(() => {
            this.updateValue(!this.isChecked);
            this.cdr.markForCheck();
          });
        });
        fromEvent(this.switchElement.nativeElement, 'keydown').pipe(takeUntil(this.destroy$)).subscribe(event => {
          if (this.nzControl || this.nzDisabled || this.nzLoading) {
            return;
          }
          const {
            keyCode
          } = event;
          if (keyCode !== LEFT_ARROW && keyCode !== RIGHT_ARROW && keyCode !== SPACE && keyCode !== ENTER) {
            return;
          }
          event.preventDefault();
          this.ngZone.run(() => {
            if (keyCode === LEFT_ARROW) {
              this.updateValue(false);
            } else if (keyCode === RIGHT_ARROW) {
              this.updateValue(true);
            } else if (keyCode === SPACE || keyCode === ENTER) {
              this.updateValue(!this.isChecked);
            }
            this.cdr.markForCheck();
          });
        });
      });
    }
    ngAfterViewInit() {
      this.focusMonitor.monitor(this.switchElement.nativeElement, true).pipe(takeUntil(this.destroy$)).subscribe(focusOrigin => {
        if (!focusOrigin) {
          /** https://github.com/angular/angular/issues/17793 **/
          Promise.resolve().then(() => this.onTouched());
        }
      });
    }
    ngOnDestroy() {
      this.focusMonitor.stopMonitoring(this.switchElement.nativeElement);
      this.destroy$.next();
      this.destroy$.complete();
    }
    writeValue(value) {
      this.isChecked = value;
      this.cdr.markForCheck();
    }
    registerOnChange(fn) {
      this.onChange = fn;
    }
    registerOnTouched(fn) {
      this.onTouched = fn;
    }
    setDisabledState(disabled) {
      this.nzDisabled = this.isNzDisableFirstChange && this.nzDisabled || disabled;
      this.isNzDisableFirstChange = false;
      this.cdr.markForCheck();
    }
    static {
      this.ɵfac = function NzSwitchComponent_Factory(t) {
        return new (t || NzSwitchComponent)(i0.ɵɵdirectiveInject(i1.NzConfigService), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i2.FocusMonitor), i0.ɵɵdirectiveInject(i3.Directionality, 8));
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NzSwitchComponent,
        selectors: [["nz-switch"]],
        viewQuery: function NzSwitchComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 7);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.switchElement = _t.first);
          }
        },
        inputs: {
          nzLoading: "nzLoading",
          nzDisabled: "nzDisabled",
          nzControl: "nzControl",
          nzCheckedChildren: "nzCheckedChildren",
          nzUnCheckedChildren: "nzUnCheckedChildren",
          nzSize: "nzSize",
          nzId: "nzId"
        },
        exportAs: ["nzSwitch"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([{
          provide: NG_VALUE_ACCESSOR,
          useExisting: forwardRef(() => NzSwitchComponent),
          multi: true
        }]), i0.ɵɵStandaloneFeature],
        decls: 9,
        vars: 16,
        consts: [["switchElement", ""], ["uncheckTemplate", ""], ["nz-wave", "", "type", "button", 1, "ant-switch", 3, "disabled", "nzWaveExtraNode"], [1, "ant-switch-handle"], ["nz-icon", "", "nzType", "loading", "class", "ant-switch-loading-icon", 4, "ngIf"], [1, "ant-switch-inner"], [4, "ngIf", "ngIfElse"], [1, "ant-click-animating-node"], ["nz-icon", "", "nzType", "loading", 1, "ant-switch-loading-icon"], [4, "nzStringTemplateOutlet"]],
        template: function NzSwitchComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "button", 2, 0)(2, "span", 3);
            i0.ɵɵtemplate(3, NzSwitchComponent_span_3_Template, 1, 0, "span", 4);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(4, "span", 5);
            i0.ɵɵtemplate(5, NzSwitchComponent_ng_container_5_Template, 2, 1, "ng-container", 6)(6, NzSwitchComponent_ng_template_6_Template, 1, 1, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
            i0.ɵɵelementEnd();
            i0.ɵɵelement(8, "div", 7);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            const uncheckTemplate_r2 = i0.ɵɵreference(7);
            i0.ɵɵclassProp("ant-switch-checked", ctx.isChecked)("ant-switch-loading", ctx.nzLoading)("ant-switch-disabled", ctx.nzDisabled)("ant-switch-small", ctx.nzSize === "small")("ant-switch-rtl", ctx.dir === "rtl");
            i0.ɵɵproperty("disabled", ctx.nzDisabled)("nzWaveExtraNode", true);
            i0.ɵɵattribute("id", ctx.nzId);
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("ngIf", ctx.nzLoading);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.isChecked)("ngIfElse", uncheckTemplate_r2);
          }
        },
        dependencies: [NzWaveModule, i4.NzWaveDirective, NzIconModule, i5.NzIconDirective, NgIf, NzOutletModule, i6.NzStringTemplateOutletDirective],
        encapsulation: 2,
        changeDetection: 0
      });
    }
  }
  __decorate([InputBoolean()], NzSwitchComponent.prototype, "nzLoading", void 0);
  __decorate([InputBoolean()], NzSwitchComponent.prototype, "nzDisabled", void 0);
  __decorate([InputBoolean()], NzSwitchComponent.prototype, "nzControl", void 0);
  __decorate([WithConfig()], NzSwitchComponent.prototype, "nzSize", void 0);
  return NzSwitchComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzSwitchModule = /*#__PURE__*/(() => {
  class NzSwitchModule {
    static {
      this.ɵfac = function NzSwitchModule_Factory(t) {
        return new (t || NzSwitchModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: NzSwitchModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [NzSwitchComponent]
      });
    }
  }
  return NzSwitchModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NzSwitchComponent, NzSwitchModule };
